import { Platform } from "react-native";

const EMBEDDED_MODAL_MAX_SCREEN_RATIO = 0.85;
// Room for the dialog's own vertical margin so a centered popup stays inside
// the iframe instead of being clipped by it.
const EMBEDDED_POPUP_FRAME_MARGIN = 48;

export const IFRAME_LAYOUT_MESSAGE_TYPE = "VDB_IFRAME_LAYOUT";

export const isEmbeddedFrame = () =>
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  window.parent !== window;

// Inside an embedded frame, `vh` and `window.innerHeight` follow the iframe,
// which is as tall as the page. Modals must be capped to the device screen.
export const getEmbeddedModalMaxHeight = () => {
  if (!isEmbeddedFrame()) return 0;
  const systemHeight = window.screen?.availHeight || window.screen?.height || 0;
  return systemHeight
    ? Math.round(systemHeight * EMBEDDED_MODAL_MAX_SCREEN_RATIO)
    : 0;
};

// The iframe is only as tall as the current page. A short search result set
// makes that shorter than the device, so the dialog has to fit the frame.
export const getEmbeddedPopupMaxHeight = (frameHeight?: number) => {
  if (!isEmbeddedFrame()) return 0;
  const screenCap = getEmbeddedModalMaxHeight();
  const frame =
    frameHeight ?? (typeof window !== "undefined" ? window.innerHeight : 0);
  const frameCap =
    frame > EMBEDDED_POPUP_FRAME_MARGIN
      ? frame - EMBEDDED_POPUP_FRAME_MARGIN
      : 0;
  if (screenCap && frameCap) return Math.min(screenCap, frameCap);
  return screenCap || frameCap;
};

// Keep a scroll region inside the dialog after header and action bar space.
export const fitEmbeddedScrollHeight = (
  preferred: number,
  chrome: number,
  frameHeight?: number
) => {
  const popupMax = getEmbeddedPopupMaxHeight(frameHeight);
  if (!popupMax) return preferred;
  const available = popupMax - chrome;
  if (available <= 0) return preferred;
  return Math.min(preferred, available);
};

export type IframeLayoutMode = "auto-height" | "internal-scroll";

export type IframeLayoutMessage = {
  type: typeof IFRAME_LAYOUT_MESSAGE_TYPE;
  mode: IframeLayoutMode;
  route: string;
  height?: number;
};

/**
 * Parent page contract:
 * - `auto-height`: set the iframe element height to `height` so the parent page scrolls.
 * - `internal-scroll`: restore the iframe's fixed height so this app scrolls inside the frame.
 * `"*"` matches the existing embed contract because customer parent origins vary.
 */
// Old embed pages never apply the posted height. Screens keep their own scroll
// until a post actually changes this frame, which only a parent with the
// layout script can do.
type ParentResizeListener = (resizes: boolean) => void;

let parentResizes: boolean | null = null;
let parentResizeTimer = 0;
const parentResizeListeners = new Set<ParentResizeListener>();

const setParentResizes = (resizes: boolean) => {
  if (parentResizes === resizes) return;
  parentResizes = resizes;
  parentResizeListeners.forEach((listener) => listener(resizes));
};

const watchParentResize = (height: number) => {
  if (parentResizes === true || typeof window === "undefined") return;
  const before = window.innerHeight;
  if (Math.abs(before - height) < 8) return;

  const onResize = () => {
    if (Math.abs(window.innerHeight - before) < 8) return;
    window.removeEventListener("resize", onResize);
    window.clearTimeout(parentResizeTimer);
    setParentResizes(true);
  };

  window.addEventListener("resize", onResize);
  window.clearTimeout(parentResizeTimer);
  parentResizeTimer = window.setTimeout(() => {
    window.removeEventListener("resize", onResize);
    if (parentResizes !== true) setParentResizes(false);
  }, 700);
};

export const parentResizesIframe = () => parentResizes === true;

export const subscribeParentResizesIframe = (
  listener: ParentResizeListener
) => {
  parentResizeListeners.add(listener);
  return () => {
    parentResizeListeners.delete(listener);
  };
};

export const postIframeLayout = ({
  mode,
  route,
  height,
}: Omit<IframeLayoutMessage, "type">) => {
  if (Platform.OS !== "web" || typeof window === "undefined") return;
  if (window.parent === window) return;

  const message: IframeLayoutMessage = {
    type: IFRAME_LAYOUT_MESSAGE_TYPE,
    mode,
    route,
    ...(height != null ? { height } : {}),
  };

  window.parent.postMessage(message, "*");
  if (mode === "auto-height" && height != null) watchParentResize(height);
};

// The dialog is centered in the tall iframe. Desktop browsers scroll the parent
// page to it on open. Mobile browsers do not, so the parent has to.
export const IFRAME_SCROLL_INTO_VIEW_MESSAGE_TYPE =
  "VDB_IFRAME_SCROLL_INTO_VIEW";

export const postIframeScrollIntoView = ({
  top,
  height,
}: {
  top: number;
  height: number;
}) => {
  if (!isEmbeddedFrame()) return;
  if (!Number.isFinite(top) || !Number.isFinite(height) || height <= 0) return;
  window.parent.postMessage(
    {
      type: IFRAME_SCROLL_INTO_VIEW_MESSAGE_TYPE,
      top: Math.round(top),
      height: Math.round(height),
    },
    "*"
  );
};

// A new screen, or going back, should start at the top of the embed. The
// parent page is the scroller, so it has to move.
export const postIframeScrollToTop = () => {
  if (!isEmbeddedFrame()) return;
  window.scrollTo(0, 0);
  window.parent.postMessage(
    {
      type: IFRAME_SCROLL_INTO_VIEW_MESSAGE_TYPE,
      top: 0,
      align: "start",
    },
    "*"
  );
};
